import React, { useEffect, useMemo, useState } from "react";

const API =
  process.env.NODE_ENV === "production"
    ? "https://ecovanta.onrender.com"
    : "http://localhost:5000";

function buildRoadmap(latestAssessment) {
  if (!latestAssessment?.dimensionScores) return [];

  const sortedGaps = [...latestAssessment.dimensionScores]
    .sort((a, b) => a.score - b.score)
    .slice(0, 3);

  const priorities = sortedGaps.map((d) => d.name);

  return [
    {
      phase: "First 30 Days",
      actions: [
        `Assign ownership for ${priorities[0] || "ESG Governance"}.`,
        "Establish an ESG steering group.",
        "Review current ESG policies and responsibilities.",
        "Define baseline ESG KPIs."
      ]
    },
    {
      phase: "Days 31–60",
      actions: [
        `Develop improvement initiatives for ${priorities[1] || "Strategy"}.`,
        "Implement ESG data collection processes.",
        "Create management reporting dashboards.",
        "Set measurable ESG targets."
      ]
    },
    {
      phase: "Days 61–90",
      actions: [
        `Address maturity gaps in ${priorities[2] || "Risk Management"}.`,
        "Review progress against ESG objectives.",
        "Prepare executive ESG performance review.",
        "Define next-stage ESG maturity goals."
      ]
    }
  ];
}

export default function ESGRoadmapPreview() {
  const [assessments, setAssessments] = useState([]);
  const [status, setStatus] = useState("Loading roadmap preview...");

  useEffect(() => {
    async function loadAssessments() {
      try {
        const response = await fetch(`${API}/maturity-assessments`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.error || "Unable to load assessments.");
        }

        setAssessments(Array.isArray(data) ? data : []);
        setStatus("");
      } catch (error) {
        console.error(error);
        setStatus(error.message);
      }
    }

    loadAssessments();
  }, []);

  const latestAssessment = assessments[0];

  const roadmap = useMemo(() => {
    return buildRoadmap(latestAssessment);
  }, [latestAssessment]);

  if (status) {
    return (
      <main style={{ maxWidth: "1000px", margin: "0 auto", padding: "40px 20px" }}>
        <h1>90-Day ESG Roadmap</h1>
        <p>{status}</p>
      </main>
    );
  }

  if (!latestAssessment) {
    return (
      <main style={{ maxWidth: "1000px", margin: "0 auto", padding: "40px 20px" }}>
        <h1>90-Day ESG Roadmap</h1>
        <p>No ESG assessment found.</p>
      </main>
    );
  }

  return (
    <main style={{ maxWidth: "1000px", margin: "0 auto", padding: "40px 20px" }}>
      <h1>90-Day ESG Roadmap</h1>

      <p>
        Preview of a tailored ESG improvement roadmap based on your latest
        ESG Maturity Benchmark assessment.
      </p>

      <div
        style={{
          padding: "20px",
          borderRadius: "12px",
          background: "#f8fafc",
          border: "1px solid #e5e7eb",
          marginBottom: "24px"
        }}
      >
        <strong>Current ESG Maturity:</strong>{" "}
        {latestAssessment.maturityLevel}
        <br />
        <strong>Overall Score:</strong>{" "}
        {latestAssessment.overallScore}/5
      </div>

      {roadmap.map((phase, index) => (
        <div
          key={phase.phase}
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "20px",
            background: "#ffffff",
            opacity: index === 0 ? 1 : 0.4
          }}
        >
          <h2>{phase.phase}</h2>

          <ul>
            {phase.actions.map((action) => (
              <li key={action}>{action}</li>
            ))}
          </ul>
        </div>
      ))}

      <div
        style={{
          marginTop: "30px",
          padding: "30px",
          borderRadius: "16px",
          border: "2px solid #0f766e",
          textAlign: "center",
          background: "#f8fafc"
        }}
      >
        <h2>Unlock the Complete 90-Day ESG Roadmap</h2>

        <p>
          Access the full roadmap, implementation guidance, milestones,
          accountability framework, and downloadable action plan.
        </p>

        <ul
          style={{
            textAlign: "left",
            display: "inline-block"
          }}
        >
          <li>Full 90-day roadmap</li>
          <li>Implementation milestones</li>
          <li>Responsibility assignments</li>
          <li>Benchmark alignment</li>
          <li>PDF export</li>
        </ul>

        <div style={{ marginTop: "20px" }}>
          <button
            onClick={() => {
              window.location.href = "/request-access";
            }}
            style={{
              padding: "12px 24px",
              borderRadius: "8px",
              border: "none",
              background: "#0f766e",
              color: "#fff",
              cursor: "pointer",
              fontWeight: 600
            }}
          >
            Unlock Roadmap Pro
          </button>
        </div>
      </div>
    </main>
  );
}